import type { LayersList, PickingInfo } from "@deck.gl/core";
import { WebMercatorViewport } from "@deck.gl/core";
import DeckGL, { type DeckGLRef } from "@deck.gl/react";
import { bboxPolygon } from "@turf/bbox-polygon";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point } from "@turf/helpers";
import { useDebounce } from "@uidotdev/usehooks";
import { APIProvider, Map as GoogleMap } from "@vis.gl/react-google-maps";
import type { BBox } from "geojson";
import { useAtom } from "jotai";
import type { MjolnirGestureEvent, MjolnirPointerEvent } from "mjolnir.js";
import { useCallback, useEffect, useMemo, useRef } from "react";
import MapGL from "react-map-gl/maplibre";
import { GOOGLEMAPS_API_KEY } from "../../constants/globals";
import { hasWebGLSupport } from "../../utils/webgl";
import { basemaps, ESRIBasemapBounds } from "./basemaps";
import { ControlContainer } from "./elements/common";
import { BasemapControl } from "./elements/DataControls/BasemapControls";
import { MapScale } from "./elements/MapScale";
import { MeasureControlBase } from "./elements/MeasureControl";
import { UnsupportedMapOverlay } from "./elements/UnsupportedMapOverlay";
import { ZoomControlBase } from "./elements/ZoomAndLegend";
import { useMap } from "./hooks/mapState";
import { useMapMeasuring } from "./hooks/measuring";
import { mapStateFamily } from "./state";

interface MapBaseProps {
    mapId: string;
    layers: LayersList[];
    onLeftClick?: (mapData: {
        info: PickingInfo[];
        latitude: number;
        longitude: number;
        event: React.MouseEvent<HTMLDivElement>;
    }) => void;
    onRightClick?: (info: PickingInfo, event: MjolnirGestureEvent) => void;
    onHover?: (info: PickingInfo, event: MjolnirPointerEvent) => void;
    getTooltip?: (info: PickingInfo) => string | null;
    // Options
    showScaleControl?: boolean;
    showZoomControl?: boolean;
    showMeasureControl?: boolean;
    showBasemapControl?: boolean;
    cursor?: string;
    customPickingRange?: number;
    customPickingLimit?: number;
}

export const MapBase = (props: MapBaseProps) => {
    const deckRef = useRef<DeckGLRef>();
    const mapContainerRef = useRef<null | HTMLDivElement>(null);

    // Check if WebGL is enabled.
    const isMapSupported = hasWebGLSupport();

    // Atom to hold map state here.
    const [mapState, setMapState] = useAtom(
        mapStateFamily({ mapId: props.mapId }),
    );

    // Const map
    const { zoomIn, zoomOut } = useMap(props.mapId);

    // Measurement
    const { measureLayers, isMeasuring, toggleMeasuring } = useMapMeasuring({
        enabled: true,
    });

    // Debounce viewstate (400 ms delay)
    const debouncedViewstate = useDebounce(mapState._viewState, 400);

    // Create a viewport for the map, based on debounced map view state
    // This causes a second rerender of the map, 400 ms after the user
    // stops moving the page.
    // Consider moving this to the `setter` of mapStateFamily.
    useEffect(() => {
        // Extract the map's viewport size for correct framing of points
        const mapWidth =
            mapContainerRef.current?.offsetWidth || window.innerWidth;
        const mapHeigth =
            mapContainerRef.current?.offsetHeight || window.innerHeight;

        // Viewport
        const viewport = new WebMercatorViewport({
            ...debouncedViewstate,
            width: mapWidth,
            height: mapHeigth,
        });

        // Calculate screen boundaries
        const upperRightScreenCoordinate = viewport.unproject([mapWidth, 0]);
        const lowerLeftScreenCoordinate = viewport.unproject([0, mapHeigth]);

        // Set areaOnMap and debouncedViewstate at the same time.
        setMapState((ms) => ({
            ...ms,
            debounced: {
                viewState: debouncedViewstate,
                areaOnScreen: bboxPolygon(
                    lowerLeftScreenCoordinate.concat(
                        upperRightScreenCoordinate,
                    ) as BBox,
                )?.geometry,
                viewport,
            },
        }));
    }, [debouncedViewstate, mapContainerRef, setMapState]);

    // When map unmounts, clear debounced states and deck ref
    useEffect(() => {
        return () => {
            setMapState((ms) => ({
                ...ms,
                debounced: {},
            }));
            deckRef.current = undefined;
        };
    }, [setMapState]);

    // Map picking mechanism, exposing only data picked.
    const { customPickingRange, customPickingLimit } = props;
    const onClickHandler = useCallback(
        (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            // Get mouse position relative to the containing div
            const containerRect = evt.currentTarget.getBoundingClientRect();
            const pickInfos = deckRef.current?.pickMultipleObjects({
                x: evt.clientX - containerRect.left,
                y: evt.clientY - containerRect.top,
                // Picking radius from central click (defaults to 0 pixels)
                radius: customPickingRange || 3,
                // Select at most N items at a time (defaults to 20 items)
                depth: customPickingLimit || 20,
            });

            // Create a viewport and compute latitude and longitude
            const viewport = new WebMercatorViewport({
                ...debouncedViewstate,
                width: mapContainerRef.current?.clientWidth,
                height: mapContainerRef.current?.clientHeight,
            });
            const coordinates = viewport.unproject([
                evt.clientX - containerRect.left,
                evt.clientY - containerRect.top,
            ]);

            // If any items were picked, deduplicate items
            if (pickInfos) {
                const seen = new Set();
                return {
                    latitude: coordinates[1],
                    longitude: coordinates[0],
                    info: pickInfos.filter((item) => {
                        const criteria =
                            item.object?.properties?.id ||
                            `${item.layer.id}-${item.index}`;
                        if (seen.has(criteria)) {
                            return false;
                        } else {
                            seen.add(criteria);
                            return true; // Unique, keep it
                        }
                    }),
                };
            }

            return {
                latitude: coordinates[1],
                longitude: coordinates[0],
                info: [],
            };
        },
        [
            deckRef,
            mapContainerRef,
            customPickingRange,
            customPickingLimit,
            debouncedViewstate,
        ],
    );

    // Click detection mechanism, to avoid click events from
    // being triggered when the map is dragged.
    const handleMouseDown = useRef<number | null>(null);
    const { onLeftClick } = props;
    const handleMouseUp = useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const clickDuration = Date.now() - (handleMouseDown.current || 0);
            handleMouseDown.current = null;
            // 200 ms holding => drag action
            if (clickDuration < 200 && onLeftClick && event.button === 0) {
                onLeftClick({
                    ...onClickHandler(event),
                    event,
                });
            }
        },
        [onClickHandler, onLeftClick],
    );

    const handleMouseDownEvent = useCallback(() => {
        handleMouseDown.current = Date.now();
    }, []);

    // Basemap handler
    const basemapData = useMemo(() => {
        if (mapState.basemap === null || mapState.basemap === "") {
            if (mapState.debounced?.viewState?.zoom > 12) {
                // If outside US, use Google Maps.
                if (
                    mapState.debounced.viewState.zoom &&
                    !booleanPointInPolygon(
                        point([
                            mapState.debounced.viewState.longitude,
                            mapState.debounced.viewState.latitude,
                        ]),
                        ESRIBasemapBounds,
                    )
                ) {
                    return basemaps.googleMapsSatellite;
                }
                return basemaps.esri;
            }
            return basemaps.aershedCustom || basemaps.osm;
        }
        // Check if there's basemap data
        if (basemaps[mapState.basemap]) {
            return basemaps[mapState.basemap];
        }
        // Otherwise fall back to ESRI
        return basemaps.esri;
    }, [mapState.basemap, mapState.debounced.viewState]);

    if (!isMapSupported) {
        return <UnsupportedMapOverlay />;
    }

    return (
        <div
            ref={mapContainerRef}
            onContextMenu={(event) => {
                event.preventDefault();
            }}
            onMouseDown={handleMouseDownEvent}
            onMouseUp={handleMouseUp}
            onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
            }}
            className="w-full h-full"
        >
            <APIProvider apiKey={GOOGLEMAPS_API_KEY}>
                <DeckGL
                    ref={deckRef}
                    viewState={mapState._viewState}
                    layers={[...props.layers, measureLayers]}
                    onViewStateChange={(newViewState) => {
                        setMapState((ms) => ({
                            ...ms,
                            _viewState: {
                                ...ms._viewState,
                                longitude: newViewState.viewState.longitude,
                                latitude: newViewState.viewState.latitude,
                                zoom: Math.max(
                                    newViewState.viewState.zoom,
                                    1.5,
                                ),
                            },
                        }));
                    }}
                    onClick={(info, event) => {
                        if (event.rightButton && props.onRightClick) {
                            props.onRightClick(info, event);
                        }
                    }}
                    onHover={(info, event) => {
                        if (!isMeasuring) {
                            props.onHover?.(info, event);
                        }
                    }}
                    controller={true}
                    getCursor={({ isHovering, isDragging }) =>
                        isMeasuring
                            ? "crosshair"
                            : isDragging
                              ? "grabbing"
                              : isHovering
                                ? "pointer"
                                : props.cursor
                    }
                    getTooltip={props.getTooltip}
                >
                    {basemapData.googleMaps ? (
                        <GoogleMap
                            disableDefaultUI={true}
                            mapTypeId="hybrid"
                            viewState={mapState._viewState}
                        />
                    ) : (
                        <MapGL
                            id={props.mapId}
                            mapStyle={basemapData.mapStyle as any}
                            attributionControl={false}
                        />
                    )}
                </DeckGL>
            </APIProvider>
            <div className="absolute top-2 right-2 rounded px-2 py-0.5 bg-white bg-opacity-60 text-xs">
                {basemapData.attribution}
            </div>
            <div className="absolute bottom-2 right-2 flex gap-2 items-end pointer-events-none">
                {props.showScaleControl && (
                    <MapScale
                        latitude={mapState._viewState.latitude}
                        zoom={mapState._viewState.zoom}
                    />
                )}
                {(props.showZoomControl || props.showMeasureControl) && (
                    <ControlContainer className="flex-col gap-3 p-1 items-end pointer-events-auto">
                        {props.showZoomControl && (
                            <ZoomControlBase
                                zoomIn={zoomIn}
                                zoomOut={zoomOut}
                            />
                        )}
                        {props.showMeasureControl && (
                            <MeasureControlBase
                                measuring={isMeasuring}
                                toggleMeasure={toggleMeasuring}
                            />
                        )}
                        {props.showBasemapControl && (
                            <BasemapControl
                                basemap={mapState.basemap}
                                toggleBasemap={(newBasemap) =>
                                    setMapState((s) => ({
                                        ...s,
                                        basemap: newBasemap,
                                    }))
                                }
                            />
                        )}
                    </ControlContainer>
                )}
            </div>
        </div>
    );
};
